import { useState } from 'react'
import { motion } from 'framer-motion'
import { Download, Eye, Heart, Settings } from 'lucide-react'
import LogoTransformPanel from './LogoTransformPanel'

export default function LogoItem({ logo, index }) {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isLiked, setIsLiked] = useState(false)
  
  // Use secure_url from Cloudinary or fallback to path
  const thumbnailUrl = logo.secure_url || logo.path || `/logos/${logo.category?.toLowerCase().replace(/\s+/g, '-')}/${logo.id}/logo.svg`

  return (
    <>
      <motion.div
        initial={{ opacity: 0, scale: 0.8 }}
        whileInView={{ opacity: 1, scale: 1 }}
        viewport={{ once: true }}
        transition={{ delay: index * 0.05 }}
  className="card-hover group dark:bg-gray-900 dark:border-gray-700"
      >
  <div className="card-body dark:bg-gray-900 dark:text-white">
          {/* Logo Preview */}
          <div className="relative aspect-square bg-nitrr-surface dark:bg-gray-800 rounded-xl mb-4 flex items-center justify-center overflow-hidden">
            <motion.img
              src={thumbnailUrl}
              alt={logo.name}
              className="w-full h-full object-contain p-4 group-hover:scale-110 transition-transform duration-300"
              loading="lazy"
              onError={(e) => {
                e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="100" height="100"%3E%3Crect fill="%23f1f3f5" width="100" height="100"/%3E%3Ctext x="50" y="50" text-anchor="middle" dy=".3em" fill="%239ca3af"%3ENo Image%3C/text%3E%3C/svg%3E'
              }}
            />
            
            {/* Overlay Actions */}
            <div className="absolute inset-0 bg-black/60 flex items-center justify-center gap-3 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
              <button
                onClick={() => setIsModalOpen(true)}
                className="btn-icon bg-white/90 text-nitrr-text hover:text-nitrr-primary"
                title="Quick Preview"
              >
                <Eye size={18} />
              </button>
              <button
                onClick={() => setIsLiked(!isLiked)}
                className={`btn-icon bg-white/90 transition-colors ${
                  isLiked ? 'text-red-500' : 'text-nitrr-text hover:text-red-500'
                }`}
                title="Add to Favorites"
              >
                <Heart size={18} fill={isLiked ? 'currentColor' : 'none'} />
              </button>
            </div>
          </div>
          
          {/* Logo Info */}
          <div className="mb-4">
            <h4 className="font-semibold text-nitrr-text dark:text-white mb-1 group-hover:text-nitrr-primary transition-colors">
              {logo.name}
            </h4>
            <p className="text-sm text-nitrr-text-muted dark:text-gray-400">{logo.category}</p>
            {logo.is_svg && (
              <span className="inline-block mt-1 text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded">
                Vector
              </span>
            )}
          </div>

          {/* Actions */}
          <div className="flex items-center justify-between">
            <div className="text-xs text-nitrr-text-muted dark:text-gray-400">
              {logo.original_format?.toUpperCase() || 'HD'}
            </div>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setIsModalOpen(true)}
              className="px-4 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white text-sm font-medium rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all shadow-md hover:shadow-lg"
              title="Transform & Download"
            >
              <Download size={14} className="inline mr-1" />
              Download
            </motion.button>
          </div>
        </div>
      </motion.div>
      
      {isModalOpen && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={() => setIsModalOpen(false)}
        >
          <div onClick={(e) => e.stopPropagation()}>
            <LogoTransformPanel
              logo={logo}
              onClose={() => setIsModalOpen(false)}
            />
          </div>
        </div>
      )}
    </>
  )
}
